import {
  startOfDay,
  startOfHour,
  addHours,
  format as formatFns,
  parse,
  differenceInDays,
  millisecondsToHours,
  intervalToDuration,
  minutesToMilliseconds,
  addDays,
  setHours,
  setSeconds,
  setMilliseconds,
  setMinutes,
  addMinutes,
  parseISO,
  isSameDay,
  isBefore as isBeforeFns,
  differenceInMilliseconds,
  isValid as isValidDate,
  hoursToMilliseconds,
  subHours,
  subDays,
} from 'date-fns'

import { DAYS, HOURS, MINUTES, SECONDS, MILLISECONDS } from './constants'

import { convertFormat } from './covertFormat'

/**
 * In-house moment alternative
 * date-fnx format: https://date-fns-interactive.netlify.app/
 * moments format: https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/01-format/
 *
 * @param {String} dateString
 * @param {String} formatString
 *
 * @returns
 *
 */
export function dateFns(dateString, formatString) {
  let date = new Date()
  if (dateString) {
    if (formatString) {
      date = parse(dateString, convertFormat(formatString), new Date())
      // e.g) dateFns(new Date())
    } else if (dateString instanceof Date) {
      date = dateString
      //e.g) 20220809, 2022-08-09
    } else if (/^(\d){8}$/.test(dateString) || /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      date = new Date(parseISO(dateString))
    } else {
      date = new Date(dateString)
    }
  }

  /**
   *
   * @param {Number} amount
   * @param {hours | days} type
   * @returns this
   */
  function add(amount, type) {
    if (HOURS.includes(type)) {
      date = addHours(date, amount)
    }
    if (DAYS.includes(type)) {
      date = addDays(date, amount)
    }
    return this
  }

  function subtract(amount, type) {
    if (HOURS.includes(type)) {
      date = subHours(date, amount)
    }
    if (DAYS.includes(type)) {
      date = subDays(date, amount)
    }
    return this
  }

  /**
   *
   * @param {days | hour} type
   * @returns
   */
  function startOf(type) {
    if (DAYS.includes(type)) {
      date = startOfDay(date)
    } else if (HOURS.includes(type)) {
      date = startOfHour(date)
    }

    return this
  }

  /**
   *
   * @param {String} formatString
   * @returns {String}
   */
  function format(formatString) {
    return formatFns(date, convertFormat(formatString))
  }

  /**
   *
   * @param {String | dateFns} compDate
   * @param {days} type
   * @returns
   */
  function diff(compDate, type = 'milliseconds') {
    let compareDate = compDate?.toDate ? compDate.toDate() : compDate
    if (typeof compDate === 'string') {
      compareDate = dateFns(compDate).toDate()
    }
    if (DAYS.includes(type)) {
      return differenceInDays(date, compareDate)
    }

    if (MILLISECONDS.includes(type)) {
      return differenceInMilliseconds(date, compareDate)
    }
  }

  function toDate() {
    return date
  }

  function set(type, amount) {
    if (HOURS.includes(type)) {
      date = setHours(date, amount)
    } else if (MINUTES.includes(type)) {
      date = setMinutes(date, amount)
    } else if (SECONDS.includes(type)) {
      date = setSeconds(date, amount)
    } else if (MILLISECONDS.includes(type)) {
      date = setMilliseconds(date, amount)
    }
    return this
  }

  function isValid() {
    return isValidDate(date)
  }

  function hour() {
    return parseInt(format('H'), 10)
  }

  function isSameOrBefore(compDate) {
    return isSameDay(date, compDate) || isBeforeFns(date, compDate)
  }

  function toString() {
    return format()
  }

  function isBefore(compDate) {
    return isBeforeFns(date, compDate)
  }

  return {
    add,
    startOf,
    format,
    diff,
    toDate,
    set,
    isValid,
    hour,
    isSameOrBefore,
    toString,
    isBefore,
    subtract,
  }
}

Object.defineProperty(dateFns, 'duration', {
  /**
   *
   * @param {number} amount
   * @param {milliseconds | minutes} type
   * @returns
   */
  value: function (amount, type = 'milliseconds') {
    let milliseconds = 0
    if (typeof amount === 'number') {
      if (MINUTES.includes(type)) {
        milliseconds = minutesToMilliseconds(amount)
      }

      if (HOURS.includes(type)) {
        milliseconds = hoursToMilliseconds(amount)
      }

      if (MILLISECONDS.includes(type)) {
        milliseconds = amount
      }
    } else if (typeof amount === 'object' && (amount?.hours || amount?.minutes)) {
      milliseconds += amount?.hours && hoursToMilliseconds(amount?.hours)
      milliseconds += amount?.minutes && minutesToMilliseconds(amount?.minutes)
    }

    let interval = intervalToDuration({ start: 0, end: milliseconds })
    function asHours() {
      return millisecondsToHours(milliseconds)
    }

    function asMilliseconds() {
      return milliseconds
    }

    /**
     *
     * @param {days|hours|minutes} type
     * @returns
     */
    function get(type) {
      if (DAYS.includes(type)) {
        return interval.days
      } else if (HOURS.includes(type)) {
        return interval.hours
      } else if (MINUTES.includes(type)) {
        return interval.minutes
      }
    }

    return {
      asHours,
      get,
      asMilliseconds,
    }
  },
})

Object.defineProperty(dateFns, 'utc', {
  value: function (amount) {
    const date = new Date(amount)
    const newDate = addMinutes(date, date.getTimezoneOffset())
    return dateFns(newDate)
  },
})

export default dateFns
